import { SupabaseClient } from '@supabase/supabase-js'

interface HealthRecord {
  Date: string
  'Active Energy (kcal)': string
  'Body Fat Percentage (%)': string
  'Body Mass Index (count)': string
  'Cycling Distance (km)': string
  'Weight/Body Mass (kg)': string
}

export async function importHealthData(csvContent: string, client: SupabaseClient) {
  try {
    // Normalize line endings and split into lines
    const normalizedContent = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const lines = normalizedContent.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => line.replace(/,\s*$/, '')) // Remove trailing commas

    const headers = lines[0].split(',').map(header => header.trim())
    console.log('Headers found:', headers)

    const records: HealthRecord[] = []
    
    // Debug the raw lines
    console.log('Number of lines:', lines.length)
    console.log('First few lines:', lines.slice(0, 3))

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]
      // Split the line and remove any trailing empty values
      const values = line.split(',')
        .map(value => value.trim())
        .filter((value, index, array) => {
          // Keep non-empty values OR values that have a non-empty value after them
          return value !== '' || array.slice(index + 1).some(v => v !== '')
        })
      
      console.log(`Processing line ${i}:`, { line, values })

      if (values.length === headers.length) {
        const record: any = {}
        headers.forEach((header, index) => {
          record[header] = values[index]
        })
        records.push(record as HealthRecord)
      } else {
        console.log(`Skipping line ${i} - incorrect number of values:`, values.length, 'expected:', headers.length)
      }
    }

    console.log('Records parsed:', records)

    // Transform and prepare the data
    const entries = records
      .map(record => {
        const entry = {
          date: record['Date']?.split(' ')[0], // Extract just the date part
          weight: record['Weight/Body Mass (kg)'] ? parseFloat(record['Weight/Body Mass (kg)']) : null,
          active_energy: record['Active Energy (kcal)'] ? parseFloat(record['Active Energy (kcal)']) : null,
          body_fat_percentage: record['Body Fat Percentage (%)'] ? parseFloat(record['Body Fat Percentage (%)']) : null,
          body_mass_index: record['Body Mass Index (count)'] ? parseFloat(record['Body Mass Index (count)']) : null,
          cycling_distance: record['Cycling Distance (km)'] ? parseFloat(record['Cycling Distance (km)']) : null
        }
        console.log('Processed entry:', entry)
        return entry
      })
      .filter(entry => {
        const isValid = entry.date && entry.weight !== null
        if (!isValid) {
          console.log('Filtered out invalid entry:', entry)
        }
        return isValid
      })

    console.log('Valid entries to upload:', entries)

    // Upload data to Supabase
    const results = []
    for (const entry of entries) {
      const { data, error } = await client
        .from('weight_entries')
        .upsert(
          { 
            date: entry.date,
            weight: entry.weight,
            active_energy: entry.active_energy,
            body_fat_percentage: entry.body_fat_percentage,
            body_mass_index: entry.body_mass_index,
            cycling_distance: entry.cycling_distance
          },
          { 
            onConflict: 'date'
          }
        )

      results.push({
        date: entry.date,
        success: !error,
        error: error?.message
      })

      if (error) {
        console.error('Error uploading entry:', entry, error)
      } else {
        console.log('Successfully uploaded entry:', entry)
      }
    }

    return {
      success: true,
      results
    }
  } catch (error) {
    console.error('Error importing health data:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
} 